<template>
  <div class="service-contact-container relative min-h-screen py-12">
    <!-- Background -->
    <div class="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-700 to-gray-800 opacity-40"></div>
    <div class="absolute inset-0 bg-pattern opacity-5"></div>
    
    <!-- Header Section -->
    <div class="relative z-10 text-center mb-12">
      <h2 class="text-4xl md:text-6xl font-bold text-white mb-6">
        Request a <span class="text-primary">Call Back</span>
      </h2>
      <p class="text-xl text-white/80 mb-8">
        for Service or Parts
      </p>
      <p class="text-lg text-white/70 max-w-2xl mx-auto">
        Please provide basic information so we can contact you.
      </p>
    </div>

    <!-- Contact Form Container -->
    <div class="relative z-10 max-w-3xl mx-auto px-6">
      <div class="form-container">
        <!-- Service Request Form -->
        <div class="form-wrapper">
          <div class="form-header">
            <font-awesome-icon :icon="['fas', 'headset']" class="form-icon" />
            <h3>Service Request Form</h3>
            <p>Complete this form and we'll contact you within 24 hours</p>
          </div>

          <!-- Service Request Form -->
          <form @submit.prevent="submitServiceRequest" class="service-request-form">
            <!-- Personal Information Section -->
            <div class="form-section">
              <h4 class="section-title-small">
                <font-awesome-icon :icon="['fas', 'user']" class="section-icon" />
                Contact Information
              </h4>
              
              <div class="form-grid">
                <div class="form-group">
                  <label for="firstName">First Name *</label>
                  <input 
                    type="text" 
                    id="firstName" 
                    v-model="form.firstName" 
                    required 
                    class="form-input"
                    :class="{ 'error': errors.firstName }"
                  />
                  <span v-if="errors.firstName" class="error-message">{{ errors.firstName }}</span>
                </div>
                
                <div class="form-group">
                  <label for="lastName">Last Name *</label>
                  <input 
                    type="text" 
                    id="lastName" 
                    v-model="form.lastName" 
                    required 
                    class="form-input"
                    :class="{ 'error': errors.lastName }"
                  />
                  <span v-if="errors.lastName" class="error-message">{{ errors.lastName }}</span>
                </div>
              </div>

              <div class="form-group">
                <label for="companyName">Company Name *</label>
                <input 
                  type="text" 
                  id="companyName" 
                  v-model="form.companyName" 
                  required 
                  class="form-input"
                  :class="{ 'error': errors.companyName }"
                />
                <span v-if="errors.companyName" class="error-message">{{ errors.companyName }}</span>
              </div>

              <div class="form-grid">
                <div class="form-group">
                  <label for="email">Email Address *</label>
                  <input 
                    type="email" 
                    id="email" 
                    v-model="form.email" 
                    required 
                    class="form-input"
                    :class="{ 'error': errors.email }"
                  />
                  <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
                </div>
                
                <div class="form-group">
                  <label for="phone">Phone Number *</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    v-model="form.phone" 
                    required 
                    class="form-input"
                    :class="{ 'error': errors.phone }"
                  />
                  <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
                </div>
              </div>
            </div>

            <!-- Service Request Section -->
            <div class="form-section">
              <h4 class="section-title-small">
                <font-awesome-icon :icon="['fas', 'wrench']" class="section-icon" />
                Service Request Details
              </h4>
              
              <div class="form-group">
                <label for="inquiryType">Type of Inquiry</label>
                <select 
                  id="inquiryType" 
                  v-model="form.inquiryType" 
                  class="form-input"
                >
                  <option value="">Select inquiry type...</option>
                  <option value="Trailer Service">Trailer Service & Repair</option>
                  <option value="Parts Request">Parts Request</option>
                  <option value="DOT Inspection">DOT Inspection</option>
                  <option value="Warranty Service">Warranty Service</option>
                  <option value="Emergency Repair">Emergency Repair</option>
                  <option value="Preventive Maintenance">Preventive Maintenance</option>
                  <option value="General Question">General Question</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div class="form-group">
                <label for="inquirySummary">Detailed Summary of Your Inquiry *</label>
                <textarea 
                  id="inquirySummary" 
                  v-model="form.inquirySummary" 
                  required 
                  rows="6" 
                  class="form-textarea"
                  :class="{ 'error': errors.inquirySummary }"
                  placeholder="Please provide details about your service needs, parts requirements, or any specific issues you're experiencing with your equipment..."
                ></textarea>
                <span v-if="errors.inquirySummary" class="error-message">{{ errors.inquirySummary }}</span>
              </div>

              <div class="form-group">
                <label for="urgency">Urgency Level</label>
                <div class="radio-group">
                  <label class="radio-label">
                    <input type="radio" v-model="form.urgency" value="Emergency" />
                    <span class="radio-mark"></span>
                    Emergency
                  </label>
                  <label class="radio-label">
                    <input type="radio" v-model="form.urgency" value="Urgent" />
                    <span class="radio-mark"></span>
                    Urgent
                  </label>
                  <label class="radio-label">
                    <input type="radio" v-model="form.urgency" value="Standard" />
                    <span class="radio-mark"></span>
                    Standard
                  </label>
                  <label class="radio-label">
                    <input type="radio" v-model="form.urgency" value="Non-Urgent" />
                    <span class="radio-mark"></span>
                    Non-Urgent
                  </label>
                </div>
              </div>

              <div class="form-group">
                <label for="preferredContact">Preferred Contact Method</label>
                <div class="checkbox-group">
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="form.preferredContact" value="Phone Call" />
                    <span class="checkmark"></span>
                    Phone Call
                  </label>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="form.preferredContact" value="Email" />
                    <span class="checkmark"></span>
                    Email
                  </label>
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="form.preferredContact" value="Text Message" />
                    <span class="checkmark"></span>
                    Text Message
                  </label>
                </div>
              </div>

              <div class="form-group">
                <label for="bestTimeToCall">Best Time to Contact You</label>
                <input 
                  type="text" 
                  id="bestTimeToCall" 
                  v-model="form.bestTimeToCall" 
                  class="form-input"
                  placeholder="e.g., Weekdays 9AM-5PM, Anytime, etc."
                />
              </div>
            </div>

            <!-- reCAPTCHA -->
            <div class="form-section">
              <div class="recaptcha-container">
                <div id="service-recaptcha-container"></div>
                <span v-if="errors.recaptcha" class="error-message">{{ errors.recaptcha }}</span>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="form-submit">
              <button 
                type="submit" 
                class="submit-btn"
                :disabled="isSubmitting"
                :class="{ 'submitting': isSubmitting }"
              >
                <font-awesome-icon 
                  v-if="isSubmitting" 
                  :icon="['fas', 'spinner']" 
                  spin 
                  class="submit-icon" 
                />
                <font-awesome-icon 
                  v-else 
                  :icon="['fas', 'paper-plane']" 
                  class="submit-icon" 
                />
                <span>{{ isSubmitting ? 'Sending Request...' : 'Submit Service Request' }}</span>
              </button>
            </div>
          </form>

          <!-- Success Message -->
          <div v-if="submitSuccess" class="success-message">
            <div class="success-content">
              <font-awesome-icon :icon="['fas', 'check-circle']" class="success-icon" />
              <h3>Service Request Submitted Successfully!</h3>
              <p>Thank you for contacting STE Tank & Equipment. Our service team will review your request and contact you as soon as we are able.</p>
              <div class="success-details">
                <p><strong>Reference Number:</strong> STE-{{ Date.now().toString().slice(-6) }}</p>
                <p><strong>Submitted:</strong> {{ new Date().toLocaleDateString() }} at {{ new Date().toLocaleTimeString() }}</p>
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="submitError" class="error-message-container">
            <div class="error-content">
              <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="error-icon" />
              <h3>Submission Error</h3>
              <p>{{ submitError }}</p>
              <button @click="submitError = ''" class="error-dismiss">Try Again</button>
            </div>
          </div>
        </div>
        
        <!-- Contact Information -->
        <div class="contact-info">
          <h3>Direct Contact</h3>
          <div class="contact-item">
            <font-awesome-icon :icon="['fas', 'phone']" />
            <a href="tel:+16083651200">(608) 313-8039</a>
          </div>
          <div class="contact-item">
            <font-awesome-icon :icon="['fas', 'envelope']" />
            <a href="mailto:service@ste-usa.com">service@ste-usa.com</a>
          </div>
          <div class="contact-item">
            <font-awesome-icon :icon="['fas', 'clock']" />
            <span>Mon-Fri: 7:00 AM - 5:00 PM CST</span>
          </div>
          <div class="contact-item">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
            <span><strong>Emergency Service:</strong> Call main number</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import emailjs from '@emailjs/browser';

export default {
  name: 'ServiceContactSection',
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        phone: '',
        inquiryType: '',
        inquirySummary: '',
        urgency: 'Standard',
        preferredContact: [],
        bestTimeToCall: ''
      },
      errors: {},
      isSubmitting: false,
      submitSuccess: false,
      submitError: '',
      recaptchaLoaded: false,
      recaptchaResponse: ''
    };
  },
  mounted() {
    this.loadRecaptcha();
  },
  methods: {
    loadRecaptcha() {
      // Load Google reCAPTCHA script
      if (!window.grecaptcha) {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?onload=onServiceRecaptchaLoad&render=explicit';
        script.async = true;
        script.defer = true;
        
        // Define global callback
        window.onServiceRecaptchaLoad = () => {
          this.renderRecaptcha();
        };
        
        document.head.appendChild(script);
      } else {
        this.renderRecaptcha();
      }
    },

    renderRecaptcha() {
      if (window.grecaptcha && document.getElementById('service-recaptcha-container')) {
        window.grecaptcha.render('service-recaptcha-container', {
          'sitekey': '6LfnNl4rAAAAACOnTCwnPDYztVqtjQuSIdsDNPNk', // Replace with your actual site key
          'callback': (response) => {
            this.recaptchaResponse = response;
            this.errors.recaptcha = '';
          },
          'expired-callback': () => {
            this.recaptchaResponse = '';
          }
        });
        this.recaptchaLoaded = true;
      }
    },

    validateForm() {
      this.errors = {};
      
      // Required field validation
      const requiredFields = [
        'firstName', 'lastName', 'companyName', 'email', 'phone', 'inquirySummary'
      ];
      
      requiredFields.forEach(field => {
        if (!this.form[field] || this.form[field].trim() === '') {
          this.errors[field] = 'This field is required';
        }
      });

      // Email validation
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (this.form.email && !emailPattern.test(this.form.email)) {
        this.errors.email = 'Please enter a valid email address';
      }

      // Phone validation
      const phonePattern = /^[\+]?[1-9][\d]{0,15}$/;
      if (this.form.phone && !phonePattern.test(this.form.phone.replace(/\D/g, ''))) {
        this.errors.phone = 'Please enter a valid phone number';
      }

      // reCAPTCHA validation
      if (!this.recaptchaResponse) {
        this.errors.recaptcha = 'Please complete the reCAPTCHA verification';
      }

      return Object.keys(this.errors).length === 0;
    },

    async submitServiceRequest() {
      if (!this.validateForm()) {
        // Scroll to first error
        const firstError = Object.keys(this.errors)[0];
        const errorElement = document.getElementById(firstError);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      this.isSubmitting = true;
      this.submitError = '';

      try {
        // Prepare form data for EmailJS
        const templateParams = {
          to_email: 'service@ste-usa.com', // Replace with your service email
          from_name: `${this.form.firstName} ${this.form.lastName}`,
          company_name: this.form.companyName,
          contact_email: this.form.email,
          contact_phone: this.form.phone,
          inquiry_type: this.form.inquiryType || 'General Inquiry',
          inquiry_summary: this.form.inquirySummary,
          urgency_level: this.form.urgency,
          preferred_contact: this.form.preferredContact.join(', ') || 'Any method',
          best_time_to_call: this.form.bestTimeToCall || 'Not specified',
          submission_date: new Date().toLocaleDateString(),
          submission_time: new Date().toLocaleTimeString(),
          reference_number: `STE-${Date.now().toString().slice(-6)}`
        };

        // Send email using EmailJS
        await emailjs.send(
          'service_l1abw81', // Replace with your EmailJS service ID
          'template_i3u1xsm', // Replace with your EmailJS template ID for service requests
          templateParams,
          'NHRxQRBTZkiJcFeub' // Replace with your EmailJS public key
        );

        this.submitSuccess = true;
        this.resetForm();
        
        // Scroll to success message
        setTimeout(() => {
          document.querySelector('.success-message').scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }, 100);

      } catch (error) {
        console.error('Service request submission error:', error);
        this.submitError = 'There was an error submitting your service request. Please try again or contact us directly at (608) 365-1200.';
      } finally {
        this.isSubmitting = false;
      }
    },

    resetForm() {
      this.form = {
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        phone: '',
        inquiryType: '',
        inquirySummary: '',
        urgency: 'Standard',
        preferredContact: [],
        bestTimeToCall: ''
      };
      
      this.errors = {};
      
      // Reset reCAPTCHA
      if (window.grecaptcha) {
        window.grecaptcha.reset();
        this.recaptchaResponse = '';
      }
    }
  }
};
</script>

<style scoped>
.bg-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.form-container {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.form-wrapper {
  margin-bottom: 2rem;
}

.form-header {
  text-align: center;
  margin-bottom: 2rem;
}

.form-icon {
  font-size: 2rem;
  color: #a22a21;
  margin-bottom: 1rem;
}

.form-header h3 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
}

.form-header p {
  color: rgba(183, 185, 186, 0.8);
  font-size: 1rem;
}

/* Form Styles */
.service-request-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-section {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.section-title-small {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.2rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(162, 42, 33, 0.3);
}

.section-icon {
  color: #a22a21;
  font-size: 1.1rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
}

.form-input,
.form-textarea {
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #ffffff;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: rgba(162, 42, 33, 0.8);
  box-shadow: 0 0 0 2px rgba(162, 42, 33, 0.2);
  background: rgba(255, 255, 255, 0.08);
}

/* Fix select dropdown styling */
.form-input select,
select.form-input {
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  cursor: pointer;
}

.form-input option,
select.form-input option {
  background: #2a2a2a;
  color: #ffffff;
  padding: 0.5rem;
  border: none;
}

.form-input option:hover,
select.form-input option:hover {
  background: #3a3a3a;
  color: #ffffff;
}

.form-input option:checked,
select.form-input option:checked {
  background: #a22a21;
  color: #ffffff;
}

.form-input.error,
.form-textarea.error {
  border-color: #ff4444;
  box-shadow: 0 0 0 2px rgba(255, 68, 68, 0.2);
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.error-message {
  color: #ff4444;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

/* Checkbox and Radio Styles */
.checkbox-group,
.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
}

.checkbox-label,
.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  transition: color 0.3s ease;
}

.checkbox-label:hover,
.radio-label:hover {
  color: #ffffff;
}

.checkbox-label input[type="checkbox"],
.radio-label input[type="radio"] {
  opacity: 0;
  position: absolute;
}

.checkmark,
.radio-mark {
  width: 18px;
  height: 18px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  transition: all 0.3s ease;
  position: relative;
}

.radio-mark {
  border-radius: 50%;
}

.checkbox-label input[type="checkbox"]:checked + .checkmark,
.radio-label input[type="radio"]:checked + .radio-mark {
  background: #a22a21;
  border-color: #a22a21;
}

.checkbox-label input[type="checkbox"]:checked + .checkmark::after {
  content: "✓";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.radio-label input[type="radio"]:checked + .radio-mark::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
}

/* reCAPTCHA Styles */
.recaptcha-container {
  display: flex;
  justify-content: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Submit Button */
.form-submit {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.submit-btn {
  background: linear-gradient(135deg, #a22a21 0%, #c23529 100%);
  border: none;
  color: #ffffff;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 200px;
  justify-content: center;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(162, 42, 33, 0.4);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.submit-btn.submitting {
  background: linear-gradient(135deg, #666666 0%, #888888 100%);
}

.submit-icon {
  font-size: 1rem;
}

/* Success/Error Messages */
.success-message,
.error-message-container {
  margin-top: 2rem;
  padding: 2rem;
  border-radius: 8px;
  text-align: center;
}

.success-message {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.error-message-container {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.success-content,
.error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.success-icon {
  font-size: 3rem;
  color: #22c55e;
}

.error-icon {
  font-size: 3rem;
  color: #ef4444;
}

.success-content h3,
.error-content h3 {
  font-size: 1.5rem;
  color: #ffffff;
  margin: 0;
}

.success-content p,
.error-content p {
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: 1.5;
}

.success-details {
  background: rgba(0, 0, 0, 0.2);
  padding: 1rem;
  border-radius: 6px;
  margin-top: 1rem;
}

.success-details p {
  margin: 0.25rem 0;
  font-size: 0.9rem;
}

.error-dismiss {
  background: #ef4444;
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.error-dismiss:hover {
  background: #dc2626;
}

/* Contact Info */
.contact-info {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 2rem;
}

.contact-info h3 {
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0.75rem;
  font-size: 1rem;
}

.contact-item svg {
  color: #a22a21;
  width: 1.25rem;
}

.contact-item a {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.3s ease;
}

.contact-item a:hover {
  color: #ffffff;
  text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 768px) {
  .form-container {
    padding: 1.5rem;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .checkbox-group,
  .radio-group {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .form-section {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .form-section {
    padding: 0.75rem;
  }
}
</style>
